import { useEffect, useRef } from 'react';
import { AnalyticsClient } from './AnalyticsClient';

// Minimal session id without external types
function generateSessionId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Debounce delay for analytics events
const ANALYTICS_DEBOUNCE_MS = 1000; // 1 second

export function useAnalytics(
  client: AnalyticsClient,
  route: string,
  authResolved: boolean,
  isAuthenticated: boolean
) {
  const sessionIdRef = useRef<string>(generateSessionId());
  const sessionStartedRef = useRef<boolean>(false);
  const lastPageViewRef = useRef<string>('');
  const pageViewTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Session start - ONLY ONCE per app load
  useEffect(() => {
    // Only send session_start if we haven't already
    if (sessionStartedRef.current) {
      return;
    }
    
    sessionStartedRef.current = true;
    console.log('📊 Analytics: Session started');
    
    client.send({ 
      event_type: 'session_start', 
      session_id: sessionIdRef.current 
    });

    // Session end handlers
    const onUnload = () => {
      console.log('📊 Analytics: Session ended');
      client.send({ 
        event_type: 'session_end', 
        session_id: sessionIdRef.current 
      });
    };
    
    window.addEventListener('beforeunload', onUnload);
    
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      client.send({ 
        event_type: 'session_end', 
        session_id: sessionIdRef.current 
      });
    };
  }, []); // ← EMPTY deps - only runs once!

  // Page view - DEBOUNCED to prevent spam
  useEffect(() => {
    // Skip if same route
    if (lastPageViewRef.current === route) {
      console.log('📊 Analytics: Skipping duplicate page view for', route);
      return;
    }

    // Clear any pending page view
    if (pageViewTimerRef.current) {
      clearTimeout(pageViewTimerRef.current);
    }

    // Debounce page view events
    pageViewTimerRef.current = setTimeout(() => {
      console.log('📊 Analytics: Page view -', route);
      lastPageViewRef.current = route;
      
      client.send({ 
        event_type: 'page_view', 
        page: route, 
        session_id: sessionIdRef.current 
      });
    }, ANALYTICS_DEBOUNCE_MS);

    return () => {
      if (pageViewTimerRef.current) {
        clearTimeout(pageViewTimerRef.current);
      }
    };
  }, [route]); // ← Only route dependency, NOT client!

  return sessionIdRef.current;
}